import React, { useCallback, useState } from 'react'
import Modal from 'react-modal'
import classNames from 'classnames'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { v4 } from 'uuid'
import toast from 'react-hot-toast'

import api from 'Src/api'
import Permissions from '../Permissions'

const INVITE_USER_URL = 'v1/users/invite'

const inviteUserToastId = v4()

const userSchema = yup.object().shape({
  name: yup
    .string()
    .required('Name is required')
    .min(3, ({ min }) => `Name should contain atleast ${min} characters`)
    .max(50, ({ max }) => `Name cannot contain more than ${max} characters`),
  email: yup.string().email('Provide a valid email').required('Email is required')
})

const BUTTON_STATES = {
  ACTIVE: 'Invite',
  IN_PROGRESS: 'Inviting...'
}

const InviteUserModal = ({ isOpen, hide, refreshPage }) => {
  const [apiError, setApiError] = useState(null)

  const [buttonState, setButtonState] = useState(BUTTON_STATES.ACTIVE)

  const [userPermissions, setUserPermissions] = useState([])

  const handleTreeChange = useCallback((updatedPermissions) => {
    setUserPermissions(updatedPermissions)
  }, [])

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      name: '',
      email: ''
    }
  })

  const onClose = useCallback(hide, [hide])

  const inviteUser = async (data) => {
    const { name, email } = data

    const payload = {
      name,
      email,
      permissions: userPermissions
    }
    setButtonState(BUTTON_STATES.IN_PROGRESS)
    toast.loading('Inviting user...', {
      id: inviteUserToastId
    })
    try {
      await api.post(INVITE_USER_URL, payload).then((resp) => resp.data)
      await refreshPage()

      // Clear the name and email fields
      reset()
      setButtonState(BUTTON_STATES.ACTIVE)
      toast.success(`Invited ${name}`, {
        id: inviteUserToastId
      })
    } catch (err) {
      console.error('Error while adding the user', err)
      setApiError(err.response.data)
      setButtonState(BUTTON_STATES.ACTIVE)
      toast.error('Error while inviting user', {
        id: inviteUserToastId
      })
    }
  }

  const name = register('name')
  const email = register('email')

  const clearApiError = () => apiError && setApiError(null)

  return (
    <Modal isOpen={isOpen} contentLabel='Invite User' ariaHideApp={false} onRequestClose={onClose}>
      <div className='modal-header'>
        <h2>Invite User</h2>
        <div className='modal-close' onClick={onClose}>
          &times;
        </div>
      </div>
      {apiError && (
        <div className='alert alert-danger mt-2' role='alert'>
          {apiError.errors?.map((err, index) => {
            return (
              <span key={'InviteError' + index}>
                {err.param}: {err.message} <br />
              </span>
            )
          })}
          {apiError.message && <span>{apiError.message}</span>}
        </div>
      )}
      <div className='modal-body'>
        <form id='invite-form' onSubmit={handleSubmit(inviteUser)}>
          <div className='form-group'>
            <label>Name</label>
            <input
              className={classNames('form-control', { 'is-invalid': errors.name })}
              name='name'
              {...name}
              onChange={(e) => {
                name.onChange(e)
                clearApiError()
              }}
            />
            {errors.name && <small className='form-text text-danger'>{errors.name.message}</small>}
          </div>
          <div className='form-group'>
            <label>Email</label>
            <input
              className={classNames('form-control', { 'is-invalid': errors.email })}
              name='email'
              {...email}
              onChange={(e) => {
                name.onChange(e)
                clearApiError()
              }}
            />
            {errors.email && <small className='form-text text-danger'>{errors.email.message}</small>}
          </div>
        </form>
      </div>
      <div className='mt-2'>
        <h4>Permissions</h4>
        <Permissions permissions={userPermissions} onChange={handleTreeChange} on />
      </div>
      <button
        className={classNames('button-primary', { disabled: buttonState === BUTTON_STATES.IN_PROGRESS })}
        type='submit'
        form='invite-form'
      >
        {buttonState === BUTTON_STATES.ACTIVE && BUTTON_STATES.ACTIVE}
        {buttonState === BUTTON_STATES.IN_PROGRESS && BUTTON_STATES.IN_PROGRESS}
      </button>
    </Modal>
  )
}

export default InviteUserModal
